import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { PageSkeleton } from "@/components/PageSkeleton";
import { ScrollToTop } from "@/components/ScrollToTop";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Layout } from "./components/Layout";
import ErrorButton from "./components/ErrorButton";
import Home from "./pages/Home";

// Lazy load all pages
const About = lazy(() => import("./pages/About"));
const Programs = lazy(() => import("./pages/Programs"));
const Impact = lazy(() => import("./pages/Impact"));
const GetInvolved = lazy(() => import("./pages/GetInvolved"));
const Resources = lazy(() => import("./pages/Resources"));
const Events = lazy(() => import("./pages/Events"));
const Contact = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Certifications = lazy(() => import("./pages/Certifications"));
const CertificationVendor = lazy(() => import("./pages/CertificationVendor"));
const Podcast = lazy(() => import("./pages/Podcast"));
const StudentTalentAccelerator = lazy(() => import("./pages/StudentTalentAccelerator"));

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
            <Route path="/" element={<Layout />}>
              <Route
                index
                element={<Home />}
              />
              <Route
                path="about"
                element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <About />
                  </Suspense>
                }
              />
              <Route
                path="programs"
                element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <Programs />
                  </Suspense>
                }
              />
              <Route
                path="impact"
                element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <Impact />
                  </Suspense>
                }
              />
              <Route
                path="get-involved"
                element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <GetInvolved />
                  </Suspense>
                }
              />
              <Route
                path="resources"
                element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <Resources />
                  </Suspense>
                }
              />
              <Route
                path="events"
                element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <Events />
                  </Suspense>
                }
              />
              <Route
                path="contact"
                element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <Contact />
                  </Suspense>
                }
              />
              <Route
                path="privacy"
                element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <PrivacyPolicy />
                  </Suspense>
                }
              />
              <Route
                path="terms"
                element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <TermsOfService />
                  </Suspense>
                }
              />
              <Route
                path="cookies"
                element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <CookiePolicy />
                  </Suspense>
                }
              />
              <Route
                path="certifications"
                element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <Certifications />
                  </Suspense>
                }
              />
              <Route
                path="certifications/:vendor"
                element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <CertificationVendor />
                  </Suspense>
                }
              />
              <Route
                path="podcast"
                element={
                  <Suspense fallback={<PageSkeleton />}>
                    <Podcast />
                  </Suspense>
                }
              />
              <Route
                path="student-talent-accelerator"
                element={
                  <Suspense fallback={<PageSkeleton />}>
                    <StudentTalentAccelerator />
                  </Suspense>
                }
              />
              <Route
                path="sentry-test"
                element={
                  <div className="container mx-auto py-20 px-4 text-center">
                    <h1 className="text-2xl font-bold mb-4">Sentry Error Tracking Test</h1>
                    <p className="mb-6 text-gray-600">Click the button below to test Sentry error logging and exception tracking.</p>
                    <ErrorButton />
                  </div>
                }
              />
              <Route
                path="*"
                element={
                  <Suspense fallback={<div>Loading...</div>}>
                    <NotFound />
                  </Suspense>
                }
              />
            </Route>
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
